import React, { useEffect, useRef, useState } from "react";
import { sheetThumbUrl, sheetRasterUrl } from "@/lib/api";
import { FileText } from "lucide-react";

/**
 * SheetThumb — letterboxed WebP preview of a sheet's first page.
 *
 * Reinforces the digital plan-room identity: drawings are visible, not abstract.
 * - Loading state: industrial skeleton (no spinner clutter).
 * - Failure state: subtle sheet-icon placeholder that still feels like a sheet,
 *   not a broken-image icon (per product requirement).
 * - Optional pin overlay: marker at normalized (x,y) in 0..1 — used by the
 *   Workflow Linked Plan hero to show where the pin sits on the drawing.
 * - Optional hover preview: when `previewOnHover` is true, a large floating
 *   raster preview pops near the card after ~320ms of idle hover. Strengthens
 *   the digital plan-wall feel without making users click into the viewer.
 */
export default function SheetThumb({
  sheetId,
  className = "",
  pin = null, // {x, y, color} — normalized 0..1
  size = "md", // sm | md | lg | xl
  previewOnHover = false,
  "data-testid": testid,
}) {
  const [state, setState] = useState("loading"); // loading | ok | error

  if (!sheetId) {
    return <ThumbFallback className={className} size={size} testid={testid} />;
  }

  const dims = {
    sm: "w-14 h-12",
    md: "w-20 h-16",
    lg: "w-32 h-24",
    xl: "w-full aspect-[4/3]",
  }[size] || "w-20 h-16";

  // overflow-visible on the wrapper only when previewOnHover so the hover tile
  // can spill out. Image clipping is handled via the inner clip wrapper.
  const overflowClass = previewOnHover ? "overflow-visible" : "overflow-hidden";

  return (
    <div
      className={`relative ${overflowClass} rounded border border-[var(--fop-border-2)] bg-[var(--fop-surface)] ${dims} ${className}`}
      data-testid={testid}
    >
      <div className="absolute inset-0 overflow-hidden rounded-[inherit]">
        {state === "loading" && <ThumbSkeleton />}
        {state === "error" && <ThumbFallback inline />}
        <img
          src={sheetThumbUrl(sheetId)}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => setState("ok")}
          onError={() => setState("error")}
          className={`absolute inset-0 w-full h-full transition-opacity duration-200 ${
            size === "xl" ? "object-contain" : "object-cover"
          } ${state === "ok" ? "opacity-100" : "opacity-0"}`}
        />
        {pin && state === "ok" && (
          <div
            className="absolute w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white shadow-md"
            style={{
              left: `${(pin.x || 0) * 100}%`,
              top: `${(pin.y || 0) * 100}%`,
              background: pin.color || "var(--fop-primary)",
            }}
            data-testid="sheet-thumb-pin-marker"
            aria-hidden
          />
        )}
      </div>
      {previewOnHover && state === "ok" && (
        <HoverPreview sheetId={sheetId} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* HoverPreview — large floating raster preview on idle hover         */
/* ------------------------------------------------------------------ */
function HoverPreview({ sheetId }) {
  const wrapRef = useRef(null);
  const timer = useRef(null);
  const [visible, setVisible] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  // Decided once when the preview opens so it doesn't jitter while the user
  // moves the mouse inside the card.
  const [anchor, setAnchor] = useState("right");

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const onEnter = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const el = wrapRef.current;
      const rect = el?.parentElement?.getBoundingClientRect();
      if (rect && typeof window !== "undefined") {
        const ROOM = 460;
        if (window.innerWidth - rect.right > ROOM) setAnchor("right");
        else if (rect.left > ROOM) setAnchor("left");
        else setAnchor("down");
      }
      setVisible(true);
    }, 320);
  };
  const onLeave = () => {
    if (timer.current) clearTimeout(timer.current);
    setVisible(false);
    setImgLoaded(false);
  };

  const posClass = {
    right: "left-full top-0 ml-3",
    left:  "right-full top-0 mr-3",
    down:  "left-0 top-full mt-3",
  }[anchor];

  return (
    <div
      ref={wrapRef}
      className="absolute inset-0 z-30"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      data-testid="sheet-thumb-hover-zone"
    >
      {visible && (
        <div
          className={`absolute ${posClass} pointer-events-none z-50 w-[440px] aspect-[4/3] bg-[#0a0a0b] rounded-md border border-[var(--fop-border-hi)] shadow-2xl overflow-hidden transition-opacity duration-150 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
          data-testid="sheet-thumb-hover-preview"
        >
          <img
            src={sheetRasterUrl(sheetId, { dpr: 1 })}
            alt=""
            decoding="async"
            onLoad={() => setImgLoaded(true)}
            className="absolute inset-0 w-full h-full object-contain"
          />
          {!imgLoaded && (
            <div className="absolute inset-0 thumb-skeleton-shimmer" />
          )}
        </div>
      )}
    </div>
  );
}

function ThumbSkeleton() {
  return (
    <div className="absolute inset-0 bg-[var(--fop-surface-2)]">
      <div className="absolute inset-0 thumb-skeleton-shimmer" />
    </div>
  );
}

function ThumbFallback({ className = "", size = "md", testid, inline = false }) {
  const dims = inline
    ? "w-full h-full"
    : ({
        sm: "w-14 h-12",
        md: "w-20 h-16",
        lg: "w-32 h-24",
        xl: "w-full aspect-[4/3]",
      }[size] || "w-20 h-16");
  return (
    <div
      className={`${inline ? "absolute inset-0" : ""} ${dims} ${
        inline ? "" : `rounded border border-[var(--fop-border-2)] ${className}`
      } bg-[var(--fop-surface)] flex flex-col items-center justify-center gap-0.5`}
      data-testid={testid}
      aria-hidden
    >
      <FileText size={16} className="text-[var(--fop-text-4)]" />
      <div className="font-mono text-[7px] uppercase tracking-wider text-[var(--fop-text-4)]">
        SHEET
      </div>
    </div>
  );
}
